/**
 * Row-selection state (Task 4) — the single, shared multi-select mechanism for
 * list tables (the queue's bulk-select + Create-batch, and any future bulk
 * action). Tracks a set of selected ids by value, so a selection survives
 * re-renders and pagination within the same view. Pure React state; the table
 * renders the checkboxes and the SelectionBar renders the count + actions.
 */
import { useCallback, useMemo, useState } from "react";

export interface Selection {
  /** Currently-selected ids. */
  ids: string[];
  count: number;
  isSelected: (id: string) => boolean;
  /** Toggle one id. */
  toggle: (id: string) => void;
  /** Add/remove a batch of ids (used by the header select-all over a page). */
  setMany: (ids: string[], on: boolean) => void;
  clear: () => void;
  /** True when every id in `ids` is selected (and there is at least one). */
  allOf: (ids: string[]) => boolean;
  /** True when some — but not all — of `ids` are selected. */
  someOf: (ids: string[]) => boolean;
}

export function useSelection(): Selection {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setMany = useCallback((ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return useMemo<Selection>(
    () => ({
      ids: [...selected],
      count: selected.size,
      isSelected: (id) => selected.has(id),
      toggle,
      setMany,
      clear,
      allOf: (ids) => ids.length > 0 && ids.every((id) => selected.has(id)),
      someOf: (ids) => {
        const n = ids.filter((id) => selected.has(id)).length;
        return n > 0 && n < ids.length;
      },
    }),
    [selected, toggle, setMany, clear],
  );
}
